/**
 * Which clock a site keeps, and how sure we are of it.
 *
 * The schedule form used to send `Intl.DateTimeFormat().resolvedOptions()
 * .timeZone` — the OPERATOR's zone. That is only ever right by
 * coincidence. `platform_admin` sees every site regardless of
 * assignment, so a London engineer setting up "daily 09:00" for a
 * Singapore hotel wrote a rule that fires at 16:00 local and then moves
 * itself to 17:00 on the day the UK falls back, at a site that has never
 * observed DST. Nothing rendered the zone, so nothing said so.
 *
 * `SiteSummary.timezone` is the answer and comes from the `sites` row
 * (set at commission). It is optional on the wire only because a server
 * or mock predating the field omits it — and when it IS missing the
 * fallback is still the browser's zone, because there is nothing else to
 * fall back to. What changed is that the fallback is now LABELLED:
 * `source` tells the caller whether the zone is the site's or a guess,
 * and every surface that shows a rule shows the zone with it.
 */
import type { SiteSummary } from '../../api/v1';
import { browserTimeZone, isKnownTimeZone } from './zoned-time';

export interface SiteTimeZone {
  /** IANA zone to expand, render and write schedules in. */
  timeZone: string;
  source: 'site' | 'browser';
}

/**
 * The zone to use for `siteId`, given the site list `SiteContext` holds.
 *
 * A zone string the runtime does not recognise is treated as absent
 * rather than passed on: `Intl` throws on an unknown zone, and a throw
 * inside a calendar render takes the whole screen down over a typo in a
 * commissioning form.
 */
export function resolveSiteTimeZone(
  sites: ReadonlyArray<SiteSummary> | null | undefined,
  siteId: string | null | undefined,
): SiteTimeZone {
  // `sites` is typed non-null on `SiteContextValue` and is nonetheless
  // absent from several context doubles in this repo. Guarded rather
  // than trusted: a screen that white-screens because a list has not
  // arrived is a far worse failure than one that falls back to the
  // browser's zone and says so, which is what this function is for.
  const list = Array.isArray(sites) ? sites : [];
  const declared = siteId ? list.find((s) => s.site === siteId)?.timezone : undefined;
  if (declared && isKnownTimeZone(declared)) {
    return { timeZone: declared, source: 'site' };
  }
  return { timeZone: browserTimeZone(), source: 'browser' };
}

/** What to say when the zone is a guess, and nothing when it is not. */
export function siteTimeZoneWarning(resolved: SiteTimeZone): string | null {
  if (resolved.source === 'site') return null;
  return (
    `This site has not declared a timezone, so times are being read and written in ` +
    `THIS BROWSER's zone (${resolved.timeZone}). If the site is somewhere else, every ` +
    `schedule written here lands at the wrong hour. Set the site's timezone when ` +
    `commissioning it.`
  );
}
